import { inject, Injectable } from "@angular/core";
import { CustomFormArray, CustomFormControl, CustomFormGroup } from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import { ItemDefault, ItemFields } from "src/app/features/items/models/items-form.model";
import { ItemPrintersFields, ItemPrintersFormGroups, ItemPrintersValidators, ItemsFormWithCustomPrinters, ItemsPrintersFormArray } from "./item-printers.model";
import { Items, ItemsPrinters, Printers } from "tilby-models";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";


@Injectable()
export class ItemPrinterFormService {

    private readonly itemDataCatalogService = inject(ItemDataCatalogService);

    private itemPrinterForm!: ItemPrintersFormGroups;

    get form() {
        return this.itemPrinterForm;
    }

    printersByCategory: Map<number, Printers[]> = new Map();

    validators: ItemPrintersValidators = {
        // --------
        provMinLength: 0,
        provMaxLength: 255,
        provMaxLengthArray: new Array(10).fill(2)
    };

    createForm(item: ItemFields = new ItemDefault(), hiddenFields?: Array<keyof ItemFields>) {
        this.printersByCategory = this.itemDataCatalogService.getPrintersByCategory() || new Map();

        this.itemPrinterForm = new CustomFormGroup<ItemsFormWithCustomPrinters>({
            printers: new CustomFormArray<CustomFormGroup<ItemsPrintersFormArray>>(
                []
            )
        })

        this.createPrinters();
        if (item.printers?.length) this.initPrinters(item.printers);

        return this.itemPrinterForm;
    }

    resetFormValues(item: Items = new ItemDefault()) {
        this.printersArray?.controls.forEach(control => {
            control.get('_enabled')?.setValue(false);
        })

        if (item.printers?.length) this.initPrinters(item.printers);

        this.itemPrinterForm.markAsPristine();
    }

    private createPrinters() {
        const printersList: Printers[] = this.itemDataCatalogService.getDataByKey("printers") || [];
        printersList.forEach(printer => {
            this.printersArray?.push(new CustomFormGroup<ItemsPrintersFormArray>({
                id: new CustomFormControl(
                    printer.id || null
                ),
                name: new CustomFormControl(
                    printer.name || null
                ),
                _enabled: new CustomFormControl(
                    false
                ),
                _categoryEnabled: new CustomFormControl(
                    false
                )
            }))
        })
    }

    private initPrinters(printers: ItemsPrinters[]) {
        printers.forEach(printer => {
            const printerIndex = this.printersArray?.value.findIndex(p => p.id === printer.id);
            if(printerIndex != undefined && printerIndex >= 0) {
                this.getEnabledPrinter(printerIndex)?.setValue(true);
            }
        })
    }

    updateCategoryField(categoryId: number) {
        if (categoryId >= 0) {
            // Recupero le stampanti associate alla categoria selezionata
            const printersInSelectedCategory = this.printersByCategory.get(categoryId);

            this.printersArray?.value.forEach((printer, index) => {
                const isPrinterEnabled = printersInSelectedCategory?.some(p => p.id === printer.id) || false;

                // Assegno il valore a _categoryEnabled
                this.getCategoryEnabledPrinter(index)?.setValue(isPrinterEnabled, {emitEvent: false});
            });
        }
    }

    getPrintersFields(currentItem: Items): Partial<Items> {
        // Clono l'oggetto di destinazione per non mutare l'originale
        const updatedItem = { ...currentItem }; 

        // Recupero tutte le chiavi (nomi dei campi) presenti nel form
        const itemPrintersFieldsToMap = Object.keys(this.itemPrinterForm.controls);

        // Ciclo su tutte le chiavi trovate nel form
        itemPrintersFieldsToMap.forEach(key => {

            const control = this.itemPrinterForm.get(key);

            if(control) {
                let value = control.value;

                if (key === 'printers' && Array.isArray(value)) {
                    value = value
                        // filtro solo le stampanti selezionate
                        ?.filter((printer: ItemPrintersFields) => printer._enabled) 
                        // costruisco l'oggetto
                        .map(({id}) => ({
                            id
                    }));
                }

                // Assegno il valore all'oggetto
                (updatedItem as any)[key] = value
            }
        })
        return updatedItem;
    }

    get printersArray() {
        return this.itemPrinterForm?.controls.printers;
    }

    getIdPrinter(index: number) {
        return this.printersArray?.at(index).controls.id;
    }

    getNamePrinter(index: number) {
        return this.printersArray?.at(index).controls.name;
    }

    getEnabledPrinter(index: number) {
        return this.printersArray?.at(index).controls._enabled;
    }

    getCategoryEnabledPrinter(index: number) {
        return this.printersArray?.at(index).controls._categoryEnabled;
    }
}